import { useState, useEffect } from "react";
import { Download, X } from "lucide-react";

/** Hook: gestisce il prompt di installazione; il worker è registrato globalmente. */
export function usePWA() {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check se gia installata
    if (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone) {
      setIsInstalled(true);
    }

    // Intercetta beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // Detect appinstalled
    window.addEventListener("appinstalled", () => setIsInstalled(true));

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const install = async () => {
    if (!installPrompt) return false;
    installPrompt.prompt();
    const result = await installPrompt.userChoice;
    if (result.outcome === "accepted") {
      setIsInstalled(true);
      setInstallPrompt(null);
      return true;
    }
    return false;
  };

  return { canInstall: !!installPrompt && !isInstalled, isInstalled, install };
}

/**
 * Hook: cambia dinamicamente il manifest in base al path
 */
export function useManifestSwitch(area) {
  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    if (!link) return;
    
    if (area === "operatore") {
      link.href = "/manifest-operatore.json";
    } else if (area === "venditore") {
      link.href = "/manifest-venditore.json";
    } else if (area === "admin") {
      link.href = "/manifest-admin.json";
    } else {
      link.href = "/manifest.json";
    }
  }, [area]);
}

/**
 * Banner installazione PWA
 */
export function PWAInstallBanner({ area = "operatore" }) {
  const { canInstall, install } = usePWA();
  const [dismissed, setDismissed] = useState(false);
  
  useManifestSwitch(area);

  // Check se gia dismissato
  useEffect(() => {
    const key = `pwa_dismissed_${area}`;
    if (localStorage.getItem(key)) setDismissed(true);
  }, [area]);

  if (!canInstall || dismissed) return null;

  const dismiss = () => {
    setDismissed(true);
    localStorage.setItem(`pwa_dismissed_${area}`, "1");
  };

  const label = area === "venditore" ? "Yollo Agente" : "Yollo Staff";

  return (
    <div className="mx-4 mb-3 p-3 rounded-2xl border border-indigo-200 bg-indigo-50 flex items-center gap-3 animate-in slide-in-from-top" data-testid="pwa-install-banner">
      <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center shrink-0">
        <Download className="h-5 w-5 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-indigo-900">Installa {label}</p>
        <p className="text-[11px] text-indigo-600">Accesso rapido dalla Home del telefono</p>
      </div>
      <button
        onClick={install}
        className="px-3 py-1.5 rounded-xl bg-indigo-600 text-white text-xs font-bold shrink-0 active:scale-95 transition-transform"
        data-testid="pwa-install-btn"
      >
        Installa
      </button>
      <button onClick={dismiss} className="p-1 text-indigo-400 hover:text-indigo-600 shrink-0" data-testid="pwa-dismiss-btn">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
